import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  CheckIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  UserIcon,
  LockClosedIcon,
  LockOpenIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  PlusIcon,
  UserPlusIcon,
  ArrowPathIcon,
  PencilIcon,
  UserCircleIcon,
} from "@heroicons/react/24/outline";
import { adminService, User } from "../../services/admin";
import { getImageUrl } from "../../services/api";

const Users: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState(
    searchParams.get("status") || "all",
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [showUserDeleteConfirm, setShowUserDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [userToBlock, setUserToBlock] = useState<User | null>(null);
  const [blockReason, setBlockReason] = useState("");

  // New user form state
  const [newUserName, setNewUserName] = useState("");
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newRegistrationCode, setNewRegistrationCode] = useState("");
  const [newUserPassword, setNewUserPassword] = useState("");
  const [newUserRole, setNewUserRole] = useState<"student" | "admin">(
    "student",
  );
  const [formError, setFormError] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [passwordStrength, setPasswordStrength] = useState<
    "weak" | "medium" | "strong" | null
  >(null);

  // Edit user form state
  const [editFormData, setEditFormData] = useState({
    name: "",
    email: "",
    registration_code: "",
    role: "student" as "student" | "admin",
    status: "pending" as "pending" | "approved" | "blocked",
    phone: "",
    age: "",
    date_of_birth: "",
    village: "",
    district: "",
    province: "",
    password: "",
  });

  useEffect(() => {
    fetchUsers();
  }, [statusFilter, currentPage]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const params: any = {
        page: currentPage,
        limit: 10,
      };

      if (statusFilter !== "all") {
        params.status = statusFilter;
      }

      if (searchTerm) {
        params.search = searchTerm;
      }

      const response = await adminService.getUsers(
        currentPage,
        20,
        statusFilter !== "all" ? statusFilter : "",
        searchTerm,
      );
      setUsers(response.users || []);
      setTotalPages(response.pagination?.totalPages || 1);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchUsers();
  };

  const handleStatusFilterChange = (status: string) => {
    setStatusFilter(status);
    setCurrentPage(1);
    if (status === "all") {
      searchParams.delete("status");
    } else {
      searchParams.set("status", status);
    }
    setSearchParams(searchParams);
  };

  const handleApprove = async (userId: number) => {
    if (!window.confirm("Are you sure you want to approve this user?")) return;

    try {
      setActionLoading(userId);
      await adminService.approveUser(userId);
      await fetchUsers();
    } catch (error) {
      console.error("Error approving user:", error);
      alert("Failed to approve user");
    } finally {
      setActionLoading(null);
    }
  };

  const handleBlock = (userId: number) => {
    const user = users.find((u) => u.id === userId) || null;
    setUserToBlock(user);
    setBlockReason("");
    setShowBlockModal(!!user);
  };

  const confirmBlockUser = async () => {
    if (!userToBlock) return;
    if (!blockReason.trim()) return;
    try {
      setActionLoading(userToBlock.id);
      await adminService.blockUser(userToBlock.id, blockReason.trim());
      setShowBlockModal(false);
      setUserToBlock(null);
      setBlockReason("");
      await fetchUsers();
    } catch (error) {
      console.error("Error blocking user:", error);
      alert("Failed to block user");
    } finally {
      setActionLoading(null);
    }
  };

  const handleUnblock = async (userId: number) => {
    if (!window.confirm("Are you sure you want to unblock this user?")) return;

    try {
      setActionLoading(userId);
      await adminService.unblockUser(userId);
      await fetchUsers();
    } catch (error) {
      console.error("Error unblocking user:", error);
      alert("Failed to unblock user");
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = (userId: number) => {
    const user = users.find((u) => u.id === userId) || null;
    setUserToDelete(user);
    setShowUserDeleteConfirm(!!user);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;
    try {
      setActionLoading(userToDelete.id);
      await adminService.deleteUser(userToDelete.id);
      setShowUserDeleteConfirm(false);
      setUserToDelete(null);
      await fetchUsers();
    } catch (error) {
      console.error("Error deleting user:", error);
      alert("Failed to delete user");
    } finally {
      setActionLoading(null);
    }
  };

  const openAddUserModal = () => {
    setNewUserName("");
    setNewUserEmail("");
    setNewRegistrationCode("");
    setNewUserPassword("");
    setNewUserRole("student");
    setFormError("");
    setShowAddUserModal(true);
  };

  const closeAddUserModal = () => {
    setShowAddUserModal(false);
  };

  const openEditUserModal = (user: User) => {
    setEditingUser(user);
    setFormError("");

    // Format date of birth for date input if available
    let formattedDate = user.date_of_birth;
    if (user.date_of_birth) {
      try {
        const date = new Date(user.date_of_birth);
        if (!isNaN(date.getTime())) {
          formattedDate = date.toISOString().split("T")[0];
        }
      } catch (e) {
        console.error("Error formatting date:", e);
      }
    }

    setEditFormData({
      name: user.name || "",
      email: user.email || "",
      registration_code: user.registration_code || "",
      role: user.role || "student",
      status: user.status || "pending",
      phone: user.phone || "",
      age: user.age?.toString() || "",
      date_of_birth: formattedDate || "",
      village: user.village || "",
      district: user.district || "",
      province: user.province || "",
      password: "", // Password field is empty by default
    });

    setShowEditUserModal(true);
  };

  const closeEditUserModal = () => {
    setShowEditUserModal(false);
    setEditingUser(null);
    setPasswordStrength(null);
  };

  const handleEditFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({ ...prev, [name]: value }));

    // Check password strength if password field is being changed
    if (name === "password") {
      if (value.length === 0) {
        setPasswordStrength(null);
      } else if (value.length < 6) {
        setPasswordStrength("weak");
      } else if (value.length < 8) {
        setPasswordStrength("medium");
      } else {
        setPasswordStrength("strong");
      }
    }
  };

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newUserName || !newUserEmail || !newUserPassword) {
      setFormError("All fields are required");
      return;
    }

    try {
      setActionLoading(-1); // Using -1 to indicate form submission loading
      setFormError("");

      console.log("Submitting form with data:", {
        name: newUserName,
        email: newUserEmail,
        registration_code: newRegistrationCode,
        password: newUserPassword,
        role: newUserRole,
      });

      await adminService.createUser({
        name: newUserName,
        email: newUserEmail,
        registration_code: newRegistrationCode,
        password: newUserPassword,
        role: newUserRole,
      });

      console.log("User created successfully");
      closeAddUserModal();
      fetchUsers();
    } catch (error: any) {
      console.error("Error creating user:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to create user";
      setFormError(errorMessage);
    } finally {
      setActionLoading(null);
    }
  };

  const handleEditUser = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!editingUser) return;

    // Confirm password change if password is provided
    if (editFormData.password) {
      const confirmPassword = window.confirm(
        `Are you sure you want to change the password for ${editingUser.name}?\n\nThis action cannot be undone.`,
      );
      if (!confirmPassword) {
        return;
      }
    }

    try {
      setActionLoading(-2); // Using -2 to indicate edit form submission loading
      setFormError("");

      const updateData: any = {};

      // Include all form fields
      updateData.name = editFormData.name;
      updateData.email = editFormData.email;
      updateData.registration_code = editFormData.registration_code;
      updateData.role = editFormData.role;
      updateData.status = editFormData.status;
      updateData.phone = editFormData.phone || undefined;
      updateData.age = editFormData.age ? Number(editFormData.age) : undefined;
      updateData.date_of_birth = editFormData.date_of_birth || undefined;
      updateData.village = editFormData.village || undefined;
      updateData.district = editFormData.district || undefined;
      updateData.province = editFormData.province || undefined;

      // Include password if provided
      if (editFormData.password) {
        updateData.password = editFormData.password;
      }

      const response = await adminService.updateUser(
        editingUser.id,
        updateData,
      );

      // Show success message
      if (editFormData.password) {
        alert("User updated successfully! Password has been changed.");
      } else {
        alert("User updated successfully!");
      }

      closeEditUserModal();
      fetchUsers();
    } catch (error: any) {
      console.error("Error updating user:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to update user";
      setFormError(errorMessage);
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "approved":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircleIcon className="mr-1 h-3 w-3" />
            ໃຊ້ງານຢູ່
          </span>
        );
      case "pending":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <ClockIcon className="mr-1 h-3 w-3" />
            ລໍຖ້າອະນຸມັດ
          </span>
        );
      case "blocked":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <XCircleIcon className="mr-1 h-3 w-3" />
            ຖືກລະງັບ
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            {status}
          </span>
        );
    }
  };

  if (loading && users.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow border border-gray-200 p-4 sm:p-6 mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
            ຈັດການຜູ້ໃຊ້ລະບົບ
          </h1>
          <p className="mt-1 text-base text-gray-600">
            ຜູ້ໃຊ້ລະບົບທັງໝົດ{" "}
            <span className="font-bold text-indigo-600">{users.length}</span>{" "}
            ຄົນ
          </p>
        </div>
        <button
          onClick={openAddUserModal}
          className="mt-2 sm:mt-0 inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow text-xs sm:text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition"
        >
          <UserPlusIcon className="h-4 w-4 mr-1" />
          ເພີ່ມຜູ້ໃຊ້ໃໝ່
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl shadow border border-gray-200 p-3 sm:p-4 mb-4">
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
          <form onSubmit={handleSearch} className="flex-1 min-w-0">
            <div className="relative rounded-lg shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-xs sm:text-sm transition"
                placeholder="ຄົ້ນຫາຜູ້ໃຊ້..."
                autoComplete="off"
              />
            </div>
          </form>
          <div className="flex items-center gap-2">
            <div className="relative w-32">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FunnelIcon className="h-4 w-4 text-gray-400" />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => handleStatusFilterChange(e.target.value)}
                className="block w-full pl-9 pr-2 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-xs sm:text-sm transition"
                title="Filter users by status"
              >
                <option value="all">ສະຖານະທັງໝົດ</option>
                <option value="pending">ລໍຖ້າອະນຸມັດ</option>
                <option value="approved">ໃຊ້ງານຢູ່</option>
                <option value="blocked">ຖືກລະງັບ</option>
              </select>
            </div>
            <button
              onClick={() => {
                setSearchTerm("");
                setStatusFilter("");
                setCurrentPage(1);
              }}
              className="inline-flex items-center px-2 py-2 border border-gray-300 shadow text-xs sm:text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition"
              title="Reset filters"
              type="button"
            >
              <ArrowPathIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mt-2 rounded-lg bg-red-50 p-2 border border-red-200 shadow text-xs sm:text-sm">
          <div className="flex items-center">
            <XCircleIcon className="h-4 w-4 text-red-400 mr-2" />
            <span className="text-red-800">{error}</span>
          </div>
        </div>
      )}

      {/* Users Table */}
      <div className="mt-4 flex flex-col">
        <div className="-mx-2 overflow-x-auto">
          <div className="inline-block min-w-full py-1 align-middle px-2">
            <div className="overflow-x-auto shadow ring-1 ring-black ring-opacity-5 rounded-xl">
              <table className="min-w-full divide-y divide-gray-200 text-xs sm:text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="py-2 pl-2 pr-2 text-left font-semibold text-gray-900">
                      ຜູ້ໃຊ້
                    </th>
                    <th className="px-2 py-2 text-left font-semibold text-gray-900">
                      ລະຫັດນັກຮຽນ
                    </th>
                    <th className="px-2 py-2 text-left font-semibold text-gray-900">
                      ລະຫັດລົງທະບຽນຮຽນ
                    </th>
                    <th className="px-2 py-2 text-left font-semibold text-gray-900">
                      ສະຖານະ
                    </th>
                    <th className="px-2 py-2 text-left font-semibold text-gray-900">
                      ສ້າງເມື່ອ
                    </th>
                    <th className="px-2 py-2 text-left font-semibold text-gray-900">
                      ເຂົ້າສູ້ລະບົບຄັ້ງສຸດທ້າຍ
                    </th>
                    <th className="px-2 py-2 text-left font-semibold text-gray-900">
                      ການກະທຳ
                    </th>
                    <th className="py-2 pl-2 pr-2 text-left font-semibold text-gray-900">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {loading ? (
                    <tr>
                      <td colSpan={7} className="py-4 text-center">
                        <div className="flex justify-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                        </div>
                      </td>
                    </tr>
                  ) : users.length === 0 ? (
                    <tr>
                      <td
                        colSpan={7}
                        className="py-6 text-center text-gray-500"
                      >
                        <div className="flex flex-col items-center">
                          <UserIcon className="h-8 w-8 text-gray-400 mb-1" />
                          <p className="text-base">No users found</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    users.map((user) => (
                      <tr
                        key={user.id}
                        className="hover:bg-gray-50 transition-colors duration-100"
                      >
                        <td className="whitespace-nowrap py-2 pl-2 pr-2 font-medium text-gray-900">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-8 w-8">
                              {user.profile_image ? (
                                <img
                                  src={getImageUrl(user.profile_image)}
                                  alt="Profile"
                                  className="h-8 w-8 rounded-full object-cover border border-gray-200 shadow"
                                  loading="lazy"
                                  onError={(e) => {
                                    e.currentTarget.style.display = "none";
                                    e.currentTarget.nextElementSibling?.classList.remove(
                                      "hidden",
                                    );
                                  }}
                                />
                              ) : null}
                              <div
                                className={`h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center shadow ${user.profile_image ? "hidden" : ""}`}
                              >
                                <UserCircleIcon className="h-5 w-5 text-gray-500" />
                              </div>
                            </div>
                            <div className="ml-2 min-w-0">
                              <button
                                onClick={() => openEditUserModal(user)}
                                className="text-left hover:bg-gray-50 rounded-lg px-2 py-1 -mx-2 transition hover:shadow"
                              >
                                <div className="font-semibold text-gray-900 truncate">
                                  {user.name}
                                </div>
                                <div className="text-gray-600 truncate">
                                  {user.email}
                                </div>
                                <div className="text-gray-500 truncate">
                                  {user.role === "admin"
                                    ? "ຜູ້ບໍລິຫານ"
                                    : "ນັກຮຽນ"}
                                  {user.village && ` • ${user.village}`}
                                  {user.district && `, ${user.district}`}
                                  {user.province && `, ${user.province}`}
                                </div>
                              </button>
                            </div>
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-2 py-2 text-gray-900 font-mono">
                          {user.student_id ||
                            `STD-${String(user.id).padStart(5, "0")}`}
                        </td>
                        <td className="whitespace-nowrap px-2 py-2 text-gray-900 font-mono">
                          {user.registration_code || "-"}
                        </td>
                        <td className="whitespace-nowrap px-2 py-2">
                          {getStatusBadge(user.status)}
                        </td>
                        <td className="whitespace-nowrap px-2 py-2 text-gray-500">
                          {new Date(user.created_at).toLocaleDateString(
                            "lo-LA",
                          )}
                        </td>
                        <td className="whitespace-nowrap px-2 py-2 text-gray-500">
                          {user.last_login
                            ? new Date(user.last_login).toLocaleDateString(
                                "lo-LA",
                              )
                            : "ບໍ່ເຄີຍ"}
                        </td>
                        <td className="whitespace-nowrap px-2 py-2">
                          <div className="flex flex-wrap gap-1 justify-start">
                            {user.status === "pending" && (
                              <button
                                onClick={() => handleApprove(user.id)}
                                className="text-green-600 hover:text-green-900 p-1 rounded-lg hover:bg-green-50 transition"
                                title="Approve user"
                              >
                                <CheckCircleIcon className="h-4 w-4" />
                              </button>
                            )}
                            {user.status !== "blocked" && (
                              <button
                                onClick={() => handleBlock(user.id)}
                                className="text-red-600 hover:text-red-900 p-1 rounded-lg hover:bg-red-50 transition"
                                title="Block user"
                              >
                                <XCircleIcon className="h-4 w-4" />
                              </button>
                            )}
                            <button
                              onClick={() => openEditUserModal(user)}
                              className="text-indigo-600 hover:text-indigo-900 p-1 rounded-lg hover:bg-indigo-50 transition"
                              title="Edit user"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(user.id)}
                              className="text-red-600 hover:text-red-900 p-1 rounded-lg hover:bg-red-50 transition"
                              title="Delete user"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                        <td className="whitespace-nowrap py-2 pl-2 pr-2"></td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Pagination */}
      <div className="mt-4 bg-white rounded-2xl shadow border border-gray-200 p-3 sm:p-4">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-2">
          <div className="flex-1 flex justify-between w-full sm:hidden">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="inline-flex items-center px-3 py-2 border border-gray-300 text-xs font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 shadow transition disabled:opacity-50"
            >
              ກັບຄືນ
            </button>
            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
              className="ml-2 inline-flex items-center px-3 py-2 border border-gray-300 text-xs font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 shadow transition disabled:opacity-50"
            >
              ຕໍ່ໄປ
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between w-full">
            <div>
              <p className="text-xs text-gray-700">
                ກຳລັງສະແດງໜ້າ <span className="font-medium">{currentPage}</span>{" "}
                ຂອງໜ້າ <span className="font-medium">{totalPages}</span>
              </p>
            </div>
            <div>
              <nav
                className="relative z-0 inline-flex rounded-lg shadow -space-x-px"
                aria-label="Pagination"
              >
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPage === 1}
                  className="inline-flex items-center px-3 py-2 rounded-l-lg border border-gray-300 bg-white text-xs font-medium text-gray-500 hover:bg-gray-50 transition disabled:opacity-50"
                >
                  ກັບຄືນ
                </button>
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                  className="inline-flex items-center px-3 py-2 rounded-r-lg border border-gray-300 bg-white text-xs font-medium text-gray-500 hover:bg-gray-50 transition disabled:opacity-50"
                >
                  ຕໍ່ໄປ
                </button>
              </nav>
            </div>
          </div>
        </div>
      </div>

      {/* Add User Modal */}
      {showAddUserModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="relative w-full max-w-xs sm:max-w-md md:max-w-lg mx-auto">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="absolute top-2 right-2">
                <button
                  type="button"
                  onClick={closeAddUserModal}
                  className="bg-white rounded-lg text-gray-400 hover:text-gray-500 focus:outline-none hover:shadow transition"
                  title="Close modal"
                  aria-label="Close modal"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              <div className="px-4 pt-4 pb-2 sm:p-6">
                <h3 className="text-lg font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent mb-2">
                  ເພີ່ມຜູ້ໃຊ້ໃໝ່
                </h3>
                <form id="addUserForm" onSubmit={handleAddUser}>
                  {formError && (
                    <div className="mb-2 p-2 bg-red-50 text-red-800 rounded-lg text-xs border border-red-200">
                      {formError}
                    </div>
                  )}
                  <div className="space-y-3">
                    <div>
                      <label
                        htmlFor="name"
                        className="block text-xs font-semibold text-gray-700"
                      >
                        ຊື່
                      </label>
                      <input
                        type="text"
                        id="name"
                        value={newUserName}
                        onChange={(e) => setNewUserName(e.target.value)}
                        required
                        className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-xs transition"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="email"
                        className="block text-xs font-semibold text-gray-700"
                      >
                        ອີເມວ
                      </label>
                      <input
                        type="email"
                        id="email"
                        value={newUserEmail}
                        onChange={(e) => setNewUserEmail(e.target.value)}
                        required
                        className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-xs transition"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="registration_code"
                        className="block text-xs font-semibold text-gray-700"
                      >
                        ລະຫັດລົງທະບຽນຮຽນ
                      </label>
                      <input
                        type="text"
                        id="registration_code"
                        value={newRegistrationCode}
                        onChange={(e) => setNewRegistrationCode(e.target.value)}
                        className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-xs transition"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="password"
                        className="block text-xs font-semibold text-gray-700"
                      >
                        ລະຫັດຜ່ານ
                      </label>
                      <input
                        type="password"
                        id="password"
                        value={newUserPassword}
                        onChange={(e) => setNewUserPassword(e.target.value)}
                        required
                        minLength={6}
                        className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-xs transition"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="role"
                        className="block text-xs font-semibold text-gray-700"
                      >
                        ກຳນົດບົດບາດ
                      </label>
                      <select
                        id="role"
                        value={newUserRole}
                        onChange={(e) =>
                          setNewUserRole(e.target.value as "student" | "admin")
                        }
                        className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-xs transition"
                      >
                        <option value="student">ນັກຮຽນ</option>
                        <option value="admin">ຜູ້ເບິ່ງແຍງລະບົບ</option>
                      </select>
                    </div>
                  </div>
                </form>
              </div>
              <div className="bg-gray-50 px-4 py-3 flex flex-col sm:flex-row-reverse gap-2">
                <button
                  type="submit"
                  form="addUserForm"
                  disabled={actionLoading === -1}
                  className="w-full inline-flex justify-center rounded-lg border border-transparent shadow px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-xs font-medium text-white hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  {actionLoading === -1 ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      ກຳລັງເພີ່ມ...
                    </>
                  ) : (
                    "ເພີ່ມຜູ້ໃຊ້"
                  )}
                </button>
                <button
                  type="button"
                  onClick={closeAddUserModal}
                  className="w-full inline-flex justify-center rounded-lg border border-gray-300 shadow px-4 py-2 bg-white text-xs font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition"
                >
                  ຍົກເລີກ
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditUserModal && editingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="relative w-full max-w-xs sm:max-w-md md:max-w-2xl mx-auto">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="absolute top-2 right-2">
                <button
                  type="button"
                  onClick={closeEditUserModal}
                  className="bg-white rounded-lg text-gray-400 hover:text-gray-500 focus:outline-none"
                  title="Close modal"
                  aria-label="Close modal"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              <div className="px-4 pt-4 pb-2 sm:p-6">
                <h3 className="text-base font-bold text-gray-900 mb-2">
                  ແກ້ໄຂຂໍ້ມູນຜູ້ໃຊ້
                </h3>
                <form id="editUserForm" onSubmit={handleEditUser}>
                  {formError && (
                    <div className="mb-2 p-2 bg-red-50 text-red-800 rounded-lg text-xs border border-red-200">
                      {formError}
                    </div>
                  )}
                  <div className="space-y-2">
                    {/* Basic Information */}
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      <div>
                        <label
                          htmlFor="editName"
                          className="block text-xs font-medium text-gray-700"
                        >
                          ຊື່
                        </label>
                        <input
                          type="text"
                          id="editName"
                          name="name"
                          value={editFormData.name}
                          onChange={handleEditFormChange}
                          required
                          className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-xs"
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="editEmail"
                          className="block text-xs font-medium text-gray-700"
                        >
                          ອີເມວ
                        </label>
                        <input
                          type="email"
                          id="editEmail"
                          name="email"
                          value={editFormData.email}
                          onChange={handleEditFormChange}
                          required
                          className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-xs"
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="editRegistrationCode"
                          className="block text-xs font-medium text-gray-700"
                        >
                          ລະຫັດລົງທະບຽນຮຽນ
                        </label>
                        <input
                          type="text"
                          id="editRegistrationCode"
                          name="registration_code"
                          value={editFormData.registration_code}
                          onChange={handleEditFormChange}
                          className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-xs"
                        />
                      </div>
                    {/* Role and Status */}
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      <div>
                        <label
                          htmlFor="editRole"
                          className="block text-xs font-medium text-gray-700"
                        >
                          ບົດບາດ
                        </label>
                        <select
                          id="editRole"
                          name="role"
                          value={editFormData.role}
                          onChange={handleEditFormChange}
                          className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-xs"
                        >
                          <option value="student">ນັກຮຽນ</option>
                          <option value="admin">ຜູ້ເບິ່ງແຍງລະບົບ</option>
                        </select>
                      </div>
                      <div>
                        <label
                          htmlFor="editStatus"
                          className="block text-xs font-medium text-gray-700"
                        >
                          ສະຖານະ
                        </label>
                        <select
                          id="editStatus"
                          name="status"
                          value={editFormData.status}
                          onChange={handleEditFormChange}
                          className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-xs"
                        >
                          <option value="pending">ລໍຖ້າອະນຸມັດ</option>
                          <option value="approved">ໃຊ້ງານຢູ່</option>
                          <option value="blocked">ຖືກລະງັບ</option>
                        </select>
                      </div>
                    </div>
                    </div>
                    {/* Contact Information */}
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      <div>
                        <label
                          htmlFor="editPhone"
                          className="block text-xs font-medium text-gray-700"
                        >
                          ເບີໂທລະສັບ
                        </label>
                        <input
                          type="tel"
                          id="editPhone"
                          name="phone"
                          value={editFormData.phone}
                          onChange={handleEditFormChange}
                          className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-xs"
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="editAge"
                          className="block text-xs font-medium text-gray-700"
                        >
                          ອາຍຸ
                        </label>
                        <input
                          type="number"
                          id="editAge"
                          name="age"
                          value={editFormData.age}
                          onChange={handleEditFormChange}
                          min="0"
                          max="120"
                          className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-xs"
                        />
                      </div>
                    </div>
                    {/* Date of Birth */}
                    <div>
                      <label
                        htmlFor="editDateOfBirth"
                        className="block text-xs font-medium text-gray-700"
                      >
                        ວັນເດືອນປີເກີດ
                      </label>
                      <input
                        type="text"
                        placeholder="YYYY-MM-DD (ຫຼື ຄລິກເພື່ອເລືອກວັນທີ)"
                        id="editDateOfBirth"
                        name="date_of_birth"
                        value={editFormData.date_of_birth}
                        onChange={handleEditFormChange}
                        onFocus={(e) => {
                          e.target.type = "date";
                          e.target.click();
                        }}
                        onBlur={(e) => {
                          e.target.type = "text";
                        }}
                        className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-xs"
                      />
                      <p className="mt-1 text-xs text-gray-400">
                        ສາມາດພິມວັນທີໄດ້ ຫຼື ຄລິກເພື່ອເລືອກຈາກປະຕິທິນ
                      </p>
                    </div>
                    {/* Password Field */}
                    <div>
                      <label
                        htmlFor="editPassword"
                        className="block text-xs font-medium text-gray-700"
                      >
                        ລະຫັດຜ່ານໃໝ່ (ປະຫວ່າງຖ້າບໍ່ຕ້ອງການປ່ຽນ)
                      </label>
                      <div className="relative">
                        <input
                          type="password"
                          id="editPassword"
                          name="password"
                          value={editFormData.password}
                          onChange={handleEditFormChange}
                          minLength={6}
                          className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-xs"
                          placeholder="ປ້ອນລະຫັດຜ່ານໃໝ່ຖ້າຕ້ອງການປ່ຽນ"
                        />
                        {editFormData.password && (
                          <div className="absolute inset-y-0 right-0 pr-2 flex items-center">
                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                          </div>
                        )}
                      </div>
                      <p className="mt-1 text-xs text-gray-400">
                        ຖ້າບໍ່ປ້ອນລະຫັດຜ່ານໃໝ່ ລະຫັດຜ່ານເດີມຈະບໍ່ປ່ຽນ
                      </p>
                      {editFormData.password && (
                        <div className="mt-1 space-y-1">
                          <p className="text-xs text-green-600 font-medium">
                            ✓ ລະຫັດຜ່ານຈະຖືກປ່ຽນເມື່ອບັນທຶກ
                          </p>
                          {passwordStrength && (
                            <div className="flex items-center space-x-1">
                              <span className="text-xs text-gray-500">
                                ຄວາມເຂັ້ມແຂງ:
                              </span>
                              <span
                                className={`text-xs font-medium ${
                                  passwordStrength === "weak"
                                    ? "text-red-600"
                                    : passwordStrength === "medium"
                                      ? "text-yellow-600"
                                      : "text-green-600"
                                }`}
                              >
                                {passwordStrength === "weak"
                                  ? "ອ່ອນ"
                                  : passwordStrength === "medium"
                                    ? "ປານກາງ"
                                    : "ເຂັ້ມແຂງ"}
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    {/* Location Information */}
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                      <div>
                        <label
                          htmlFor="editVillage"
                          className="block text-xs font-medium text-gray-700"
                        >
                          ບ້ານ
                        </label>
                        <input
                          type="text"
                          id="editVillage"
                          name="village"
                          value={editFormData.village}
                          onChange={handleEditFormChange}
                          className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-xs"
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="editDistrict"
                          className="block text-xs font-medium text-gray-700"
                        >
                          ເມືອງ
                        </label>
                        <input
                          type="text"
                          id="editDistrict"
                          name="district"
                          value={editFormData.district}
                          onChange={handleEditFormChange}
                          className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-xs"
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="editProvince"
                          className="block text-xs font-medium text-gray-700"
                        >
                          ແຂວງ
                        </label>
                        <input
                          type="text"
                          id="editProvince"
                          name="province"
                          value={editFormData.province}
                          onChange={handleEditFormChange}
                          className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-xs"
                        />
                      </div>
                    </div>
                  </div>
                </form>
              </div>
              <div className="bg-gray-50 px-4 py-3 flex flex-col sm:flex-row-reverse gap-2">
                <button
                  type="submit"
                  form="editUserForm"
                  disabled={actionLoading === -2}
                  className="w-full inline-flex justify-center rounded-lg border border-transparent shadow px-4 py-2 bg-indigo-600 text-xs font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {actionLoading === -2 ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      ກຳລັງບັນທຶກ...
                    </>
                  ) : (
                    "ບັນທຶກການປ່ຽນແປງ"
                  )}
                </button>
                <button
                  type="button"
                  onClick={closeEditUserModal}
                  className="w-full inline-flex justify-center rounded-lg border border-gray-300 shadow px-4 py-2 bg-white text-xs font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  ຍົກເລີກ
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Block User Modal */}
      {showBlockModal && userToBlock && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="relative w-full max-w-xs sm:max-w-md md:max-w-lg mx-auto">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="absolute top-2 right-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowBlockModal(false);
                    setUserToBlock(null);
                    setBlockReason("");
                  }}
                  className="bg-white rounded-lg text-gray-400 hover:text-gray-500 focus:outline-none"
                  title="Close modal"
                  aria-label="Close modal"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              <div className="px-4 pt-4 pb-2 sm:p-6">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
                    <XCircleIcon className="h-5 w-5 text-red-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-bold text-gray-900 mb-1">
                      ປິດກັ້ນຜູ້ໃຊ້
                    </h3>
                    <p className="text-xs text-gray-600 mb-2">
                      ກຳລັງປິດກັ້ນ:{" "}
                      <span className="font-semibold">{userToBlock.name}</span>
                    </p>
                    <label
                      htmlFor="blockReason"
                      className="block text-xs font-medium text-gray-700 mb-1"
                    >
                      ກະລຸນາໃຫ້ເຫດຜົນສໍາລັບການປິດກັ້ນຜູ້ໃຊ້ນີ້:
                    </label>
                    <textarea
                      id="blockReason"
                      value={blockReason}
                      onChange={(e) => setBlockReason(e.target.value)}
                      rows={3}
                      className="block w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-xs"
                      placeholder="ພິມເຫດຜົນ..."
                    />
                    <p className="mt-1 text-[11px] text-gray-400">
                      ຕ້ອງໃສ່ເຫດຜົນ ແລະ ສາມາດເບິ່ງໄດ້ໃນປະຫວັດການກະທຳ.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 flex flex-col sm:flex-row-reverse gap-2">
                <button
                  type="button"
                  onClick={confirmBlockUser}
                  disabled={
                    !blockReason.trim() ||
                    (userToBlock && actionLoading === userToBlock.id)
                  }
                  className="w-full inline-flex justify-center rounded-lg border border-transparent shadow px-4 py-2 bg-red-600 text-xs font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ປິດກັ້ນຜູ້ໃຊ້
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowBlockModal(false);
                    setUserToBlock(null);
                    setBlockReason("");
                  }}
                  className="w-full inline-flex justify-center rounded-lg border border-gray-300 shadow px-4 py-2 bg-white text-xs font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  ຍົກເລີກ
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete User Confirmation Modal */}
      {showUserDeleteConfirm && userToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="relative w-full max-w-xs sm:max-w-md md:max-w-lg mx-auto">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="px-4 pt-4 pb-2 sm:p-6">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
                    <TrashIcon className="h-5 w-5 text-red-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-bold text-gray-900 mb-1">
                      ລຶບຜູ້ໃຊ້
                    </h3>
                    <p className="text-xs text-gray-700">
                      ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບ{" "}
                      <span className="font-semibold">{userToDelete.name}</span>
                      ?
                    </p>
                    <p className="text-xs text-red-600 mt-1">
                      ⚠️ ການກະທຳນີ້ບໍ່ສາມາດຍົກເລີກໄດ້.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 flex flex-col sm:flex-row-reverse gap-2">
                <button
                  type="button"
                  onClick={confirmDeleteUser}
                  disabled={userToDelete && actionLoading === userToDelete.id}
                  className="w-full inline-flex justify-center rounded-lg border border-transparent shadow px-4 py-2 bg-red-600 text-xs font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ລຶບຜູ້ໃຊ້
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowUserDeleteConfirm(false);
                    setUserToDelete(null);
                  }}
                  className="w-full inline-flex justify-center rounded-lg border border-gray-300 shadow px-4 py-2 bg-white text-xs font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  ຍົກເລີກ
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
